import React, {useCallback, useEffect, useRef} from 'react';
import {OverlayTrigger} from 'shared/components/OverlayTrigger';
import {useRoomPlannerContext} from 'components/customer/RoomPlanner/components/RoomPlannerContext';
import FloatingMenuButton, {
    FloatingMenuButtonIcon,
} from 'components/customer/RoomPlanner/components/common/FloatingMenuButton';
import {
    duplicatePlaceholderBesideSource,
    configFromPlaceholderMesh,
} from 'components/customer/RoomPlanner/helpers/placeholder/loadPlaceholderGLB';
import {recordPlaceholderAdd} from 'components/customer/RoomPlanner/helpers/history/placeholderHistory';
import CameraSetup from 'components/customer/RoomPlanner/lib/use3DRPCameraSetup';
import {
    useIsMobileLandscape,
    useIsPortraitDevices,
} from 'shared/helpers/DeviceSize';

const PlaceholderDuplicate = () => {
    const isMobileLandscape = useIsMobileLandscape();
    const isPortraitDevices = useIsPortraitDevices();
    const {
        selectedCabinetGroup,
        placeholderListRef,
        cabinetGroupListRef,
        setDuplicateHandler,
    } = useRoomPlannerContext();
    const busyRef = useRef(false);

    const handleDuplicate = useCallback(async () => {
        if (
            busyRef.current ||
            !selectedCabinetGroup?.userData?.isPlaceholder ||
            !placeholderListRef ||
            !cabinetGroupListRef
        ) {
            return;
        }

        const config = configFromPlaceholderMesh(selectedCabinetGroup);
        if (!config) return;

        const scene = CameraSetup?.scene;
        if (!scene) return;

        busyRef.current = true;
        try {
            const group = await duplicatePlaceholderBesideSource(
                selectedCabinetGroup,
                config,
                scene,
                placeholderListRef,
                cabinetGroupListRef
            );
            recordPlaceholderAdd(
                group,
                placeholderListRef,
                cabinetGroupListRef,
                'Duplicate'
            );
        } catch (error) {
            // eslint-disable-next-line no-console -- Error handling
            console.error('Error duplicating placeholder:', error);
        } finally {
            busyRef.current = false;
        }
    }, [selectedCabinetGroup, placeholderListRef, cabinetGroupListRef]);

    const handleClick = useCallback(() => {
        void handleDuplicate();
    }, [handleDuplicate]);

    // Register the duplicate handler with the context for keyboard shortcut
    useEffect(() => {
        if (setDuplicateHandler) {
            setDuplicateHandler(handleClick);
        }
        return () => setDuplicateHandler?.(null);
    }, [handleClick, setDuplicateHandler]);

    return (
        <OverlayTrigger
            placement="top"
            overlay={<strong>Duplicate (D)</strong>}>
            <FloatingMenuButton onClick={handleClick}>
                <FloatingMenuButtonIcon
                    className="icon_button"
                    color={
                        isMobileLandscape || isPortraitDevices
                            ? 'primary'
                            : '#b2bec3'
                    }
                    iconName="Options-Copy.svg"
                />
                <label>Copy</label>
            </FloatingMenuButton>
        </OverlayTrigger>
    );
};

export default PlaceholderDuplicate;
